import { useState } from "react";
import { usePerfisAdminCrud } from "@/features/usuarios/hooks/useUsuarios";
import type { PerfilCadastro, SalvarPerfilRequest } from "@/features/usuarios/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { SortableTableHead } from "@/components/SortableTableHead";
import { useOrdenacaoLocal } from "@/hooks/useOrdenacaoLocal";

const EMPTY_FORM: SalvarPerfilRequest = {
  nome: "", isInativo: false, perMaximoDesconto: 0,
};

export default function Perfis() {
  const { query, criar, atualizar, excluir } = usePerfisAdminCrud();
  const navigate = useNavigate();

  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [excluirId,    setExcluirId]    = useState<number | null>(null);
  const [editingItem,  setEditingItem]  = useState<PerfilCadastro | null>(null);
  const [form,         setForm]         = useState<SalvarPerfilRequest>(EMPTY_FORM);

  const perfis = query.data ?? [];
  const [busca, setBusca] = useState("");

  const perfisFiltrados = busca
    ? perfis.filter(p => p.nome.toLowerCase().includes(busca.toLowerCase()))
    : perfis;

  const { itensOrdenados, ...cabecalho } = useOrdenacaoLocal<PerfilCadastro, "nome" | "desconto" | "status">(
    perfisFiltrados,
    {
      nome:     p => p.nome,
      desconto: p => p.perMaximoDesconto,
      status:   p => (p.isInativo ? "Inativo" : "Ativo"),
    },
    { ordenarPor: "nome" },
  );
  const paginacao = usePaginacaoLocal(itensOrdenados);

  const openNew = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (p: PerfilCadastro) => {
    setEditingItem(p);
    setForm({ nome: p.nome, isInativo: p.isInativo, perMaximoDesconto: p.perMaximoDesconto });
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim()) { toast.error("Nome é obrigatório."); return; }
    try {
      if (editingItem) {
        await atualizar.mutateAsync({ id: editingItem.id, req: form });
        toast.success("Perfil atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("Perfil criado.");
      }
      setDialogOpen(false);
    } catch { /* global toast */ }
  };

  const handleExcluir = async () => {
    if (!excluirId) return;
    try {
      await excluir.mutateAsync(excluirId);
      toast.success("Perfil excluído.");
    } catch { /* global toast */ }
    setExcluirId(null);
  };

  if (query.isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Perfis de Acesso</h1>
          <p className="text-muted-foreground">Gerencie os perfis e suas permissões</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/config/perfis")}>
            <Shield className="h-4 w-4 mr-2" />
            Configurar Permissões
          </Button>
          <Button onClick={openNew}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Perfil
          </Button>
        </div>
      </div>

      {/* Busca */}
      <div className="max-w-sm">
        <Input
          placeholder="Buscar por nome..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead campo="nome" {...cabecalho}>Nome</SortableTableHead>
              <SortableTableHead campo="desconto" {...cabecalho} alinharDireita>% Desc. Máximo</SortableTableHead>
              <SortableTableHead campo="status" {...cabecalho} className="text-center">Status</SortableTableHead>
              <TableHead className="w-[90px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map(p => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.nome}</TableCell>
                <TableCell className="text-right">
                  {p.perMaximoDesconto > 0 ? `${p.perMaximoDesconto}%` : "—"}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={p.isInativo ? "secondary" : "success"}>
                    {p.isInativo ? "Inativo" : "Ativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setExcluirId(p.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhum perfil encontrado para esta busca." : "Nenhum perfil cadastrado."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Perfil" : "Novo Perfil"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                maxLength={60}
                value={form.nome}
                onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="desconto">% Desconto máximo</Label>
              <Input
                id="desconto"
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={form.perMaximoDesconto}
                onChange={e => setForm(f => ({ ...f, perMaximoDesconto: parseFloat(e.target.value) || 0 }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="isInativo"
                checked={form.isInativo}
                onCheckedChange={v => setForm(f => ({ ...f, isInativo: v }))}
              />
              <Label htmlFor="isInativo">Inativo</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={criar.isPending || atualizar.isPending}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AlertDialog excluir */}
      <AlertDialog open={!!excluirId} onOpenChange={() => setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir perfil</AlertDialogTitle>
            <AlertDialogDescription>
              Perfis vinculados a usuários não podem ser excluídos. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluir} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
